export enum SyncStateType {
  IDLE = 'idle',
  STARTING = 'starting',
  SYNCING = 'syncing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  OFFLINE = 'offline',
  STUCK = 'stuck'
}

// Constant object for easy access to state values
export const syncStates = {
  IDLE: 'idle' as const,
  STARTING: 'starting' as const,
  SYNCING: 'syncing' as const,
  COMPLETED: 'completed' as const,
  FAILED: 'failed' as const,
  CANCELLED: 'cancelled' as const,
  OFFLINE: 'offline' as const,
  STUCK: 'stuck' as const,
};

export enum SyncStatus {
  IDLE = 'idle',
  QUEUED = 'starting',
  STARTING = 'starting',
  PROCESSING = 'processing',
  IN_PROGRESS = 'in_progress',
  SYNCING = 'syncing', // deprecated, use PROCESSING
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  OFFLINE = 'offline'
}

export interface SyncError {
  code: string;
  message: string;
  retryable: boolean;
}

export interface SyncOperation {
  step: string;
  recordsProcessed?: number;
  estimatedTotal?: number;
  progress: number;
}

export interface SyncSubProgress {
  current?: number;
  total?: number;
  percentage?: number;
  description?: string;
}

export interface SyncStateData {
  exchangeId: string;
  state: SyncStateType;
  progress: number;
  currentStep: string;
  startTime?: Date;
  operations: SyncOperation[];
  error?: SyncError;
  isFirstTimeUser?: boolean;
}

export interface SyncProgress {
  exchangeId: string;
  exchangeName: string;
  status: SyncStatus;
  progress: number;
  currentStep: string;
  startedAt: Date;
  estimatedCompletion?: Date;
  isFirstSync: boolean;
  subCurrent?: number;
  subTotal?: number;
  subPercentage?: number;
  subDescription?: string;
}

export interface WebSocketSyncMessage {
  type: 'sync' | 'sync_progress' | 'sync_state_change';
  exchangeId: string;
  progress?: number;
  step?: string;
  status?: string;
  state?: string;
  currentStep?: string;
  timestamp: number;
  step_data?: {
    index: number;
    max_index: number;
    name: string;
    sub_progress?: SyncSubProgress;
  };
}

export type SyncEvent =
  | { type: 'START_SYNC'; data?: Partial<SyncProgressUpdate> }
  | { type: 'UPDATE_PROGRESS'; data: SyncProgressUpdate }
  | { type: 'COMPLETE_SYNC'; data?: SyncProgressUpdate }
  | { type: 'FAIL_SYNC'; data?: SyncProgressUpdate }
  | { type: 'CANCEL_SYNC'; data?: SyncProgressUpdate }
  | { type: 'GO_OFFLINE' }
  | { type: 'GO_ONLINE' }
  | { type: 'RESET' }
  | { type: 'RECOVER_PROGRESS'; data: SyncProgressUpdate }
  | { type: 'WEBSOCKET_MESSAGE'; message: WebSocketSyncMessage };

export interface SyncProgressUpdate {
  state: string;
  exchangeId?: number;
  apiKeyId?: number;
  progress?: number;
  totalProgress?: number;
  message?: string;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  totalTransactions?: number;
  processedTransactions?: number;
  subProgress?: {
    current?: number;
    total?: number;
    description?: string;
  };
}

export interface SyncContext {
  exchanges: Record<string, SyncStateData>;
  activeSyncs: SyncProgress[];
  completedSyncs: SyncProgress[];
  failedSyncs: SyncProgress[];
  isNetworkAvailable: boolean;
  lastSyncCheck: Date;
  isFirstTimeUser: boolean;
  // Additional context fields for single sync tracking
  state?: string;
  exchangeId?: number;
  apiKeyId?: number;
  progress?: number;
  totalProgress?: number;
  message?: string;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  totalTransactions?: number;
  processedTransactions?: number;
  subProgress?: {
    current?: number;
    total?: number;
    description?: string;
  };
}